import { Field, Form as FormType } from "@/models/new/forms.types"
import {
  FieldMediaAttachmentValue,
  FieldMediaEntry,
  FieldMediaTarget,
  FileAttachmentValue,
  FormResponse,
} from "@/models/new/formResponse.types"

type AttachmentGroupKind = "fileField" | "fieldMedia"

type AttachmentItem = FileAttachmentValue | FieldMediaAttachmentValue

interface AttachmentGroup {
  key: string
  locator: string
  kind: AttachmentGroupKind
  fieldId: string
  fieldName: string
  label: string
  groupLabel?: string
  instance?: number
  note?: string
  attachments: AttachmentItem[]
  target?: FieldMediaTarget
}

const excludedFieldMediaFieldNames = new Set(["incident-title", "issue-title", "inspection-title"])

const getDynamicGroupInstances = (
  responses: FormResponse["responses"],
  groupId: string,
  fieldMedia?: FormResponse["fieldMedia"],
  additionalLocators: string[] = [],
): number[] => {
  const escapedId = groupId.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
  const pattern = new RegExp(`^${escapedId}\\.(\\d+)\\.`)
  const instances = new Set<number>()

  Object.entries(responses).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return
    }

    const match = key.match(pattern)
    if (match) {
      instances.add(parseInt(match[1], 10))
    }
  })

  // A dynamic-group instance may carry field-media bundles without any matching
  // `responses` key (e.g. a geolocation-only instance, where `handleAddInstance`
  // skips writing a blank geolocation response). Enumerate those instances too so
  // their attachment groups are not silently dropped.
  if (fieldMedia) {
    Object.keys(fieldMedia).forEach((key) => {
      const match = key.match(pattern)
      if (match) {
        instances.add(parseInt(match[1], 10))
      }
    })
  }

  additionalLocators.forEach((key) => {
    const match = key.match(pattern)
    if (match) {
      instances.add(parseInt(match[1], 10))
    }
  })

  return Array.from(instances).sort((a, b) => a - b)
}

const buildFileFieldGroup = (
  locator: string,
  field: Extract<Field, { type: "file" }>,
  attachments: FileAttachmentValue[],
  groupLabel?: string,
  instance?: number,
): AttachmentGroup => ({
  key: `fileField:${locator}`,
  locator,
  kind: "fileField",
  fieldId: field.id,
  fieldName: field.name,
  label: field.label,
  groupLabel,
  instance,
  attachments,
})

const buildFieldMediaGroup = (
  locator: string,
  field: Exclude<Field, { type: "dynamicGroup" | "file" }>,
  entry: FieldMediaEntry | undefined,
  groupId?: string,
  groupLabel?: string,
  instance?: number,
): AttachmentGroup => ({
  key: `fieldMedia:${locator}`,
  locator,
  kind: "fieldMedia",
  fieldId: entry?.target.fieldId || field.id,
  fieldName: entry?.target.fieldName || field.name,
  label: entry?.target.fieldLabel || field.label,
  groupLabel,
  instance,
  note: entry?.note,
  attachments: entry?.attachments || [],
  target: entry?.target || {
    fieldId: field.id,
    fieldName: field.name,
    fieldLabel: field.label,
    ...(groupId && instance ? { groupId, instance } : {}),
  },
})

const isFieldMediaEligible = (field: Pick<Field, "type" | "name">): boolean => {
  return (
    field.type !== "dynamicGroup" &&
    field.type !== "file" &&
    field.type !== "signature" &&
    field.type !== "organization" &&
    field.type !== "datepicker" &&
    !excludedFieldMediaFieldNames.has(field.name)
  )
}

const buildGroupsForField = (
  field: FormType["fields"][number]["fields"][number],
  formResponse: FormResponse,
  selectedLocator?: string | null,
): AttachmentGroup[] => {
  if (field.type === "file") {
    const response = formResponse.responses[field.id]
    const attachments = Array.isArray(response?.value)
      ? (response.value as FileAttachmentValue[])
      : []

    return [buildFileFieldGroup(field.id, field, attachments)]
  }

  if (field.type === "dynamicGroup") {
    return getDynamicGroupInstances(
      formResponse.responses,
      field.id,
      formResponse.fieldMedia,
    ).flatMap((instance) =>
      field.fields.flatMap((groupField) => {
        const locator = `${field.id}.${instance}.${groupField.name}`

        if (groupField.type === "dynamicGroup") {
          return []
        }

        if (groupField.type === "file") {
          const response = formResponse.responses[locator]
          if (response?.type !== "file" || !Array.isArray(response.value)) {
            return []
          }

          return [
            buildFileFieldGroup(
              locator,
              groupField,
              response.value as FileAttachmentValue[],
              field.label,
              instance,
            ),
          ]
        }

        const entry = formResponse.fieldMedia?.[locator]

        if (!entry && !isFieldMediaEligible(groupField)) {
          return []
        }

        if (!entry && selectedLocator !== locator) {
          return []
        }

        return [buildFieldMediaGroup(locator, groupField, entry, field.id, field.label, instance)]
      }),
    )
  }

  const entry = formResponse.fieldMedia?.[field.id]

  if (!entry && !isFieldMediaEligible(field)) {
    return []
  }

  if (!entry && selectedLocator !== field.id) {
    return []
  }

  return [buildFieldMediaGroup(field.id, field, entry)]
}

const buildAttachmentGroups = (
  formResponse: FormResponse | null,
  form: FormType | null,
  selectedLocator?: string | null,
): AttachmentGroup[] => {
  if (!formResponse || !form) {
    return []
  }

  return form.fields.flatMap((section) =>
    section.fields.flatMap((field) => buildGroupsForField(field, formResponse, selectedLocator)),
  )
}

export type { AttachmentGroup, AttachmentGroupKind, AttachmentItem }

export { buildAttachmentGroups, getDynamicGroupInstances, isFieldMediaEligible }
